// src/encounter/visual-acuity-form.tsx
// Visual-acuity capture form (PROD-1537) — React port of Flutter visual_acuity_form.dart. Two
// sections (Distance, Near), each with Left eye (OS) + Right eye (OD). Distance captures Sc / Cc /
// Ph Sc / Ph Cc; Near captures Sc / Cc. Every entry is a Snellen numerator/denominator pair
// (fixed "20" / allowed denominator). Correction type is a single-select control.
// Controlled: the host owns the VisualAcuityData; this emits the next value on every edit.
//
// DS-ANATOMY REWORK (Samer vitals review 2026-07-23, TOTAL REVIEW verdict: the form read as a
// generic agent-draft — native <input>/<select> chrome + hand-rolled chips): every control is now
// the real DS atom — TextField (compact) numerator · Select (compact) denominator · Segmented
// correction type. Zero native form chrome, zero hand-built visuals; tokens only.
// Behavioural note: correction type moved from toggleable chips to DS Segmented — single-select,
// no deselect ("No Correction" is itself an explicit option, so un-choosing has no clinical
// meaning; Flutter's chip group defaults the same way).
import { Button, Segmented, Select, TextField } from '@scripsteam/scrips-signal-ds';
import {
  type VisualAcuityData,
  type EyeData,
  type NumeratorDenominator,
  CORRECTION_TYPES,
  type CorrectionType,
  ALLOWED_DENOMINATOR,
  emptyEye,
} from './lib/visual-acuity';

type Section = 'distance' | 'near';
type Eye = 'leftEye' | 'rightEye';
type PairKind = 'sc' | 'cc' | 'phSc' | 'phCc';

export interface VisualAcuityFormProps {
  value: VisualAcuityData;
  onChange: (next: VisualAcuityData) => void;
}

// 21-10 design (EncVisualAcuityForm): the section titles read in the label role, sentence case, secondary ink (no caps, no tracking);
// the type roles themselves are the Objective sheet's (`[data-part]`, encounter-objective.css).
const SECTION_TITLE = 'mt-0 mx-0 mb-2 text-size-xs font-medium text-text-secondary';
const EYE_LABEL =
  'text-size-sm font-semibold text-text-primary mt-0 mx-0 mb-2';
const PAIR_ROW = 'flex items-center gap-2 mt-0 mx-0 mb-2';
const KIND_LABEL = 'text-size-xs text-text-secondary';
const SLASH = 'text-text-secondary';

const DISTANCE_KINDS: { kind: PairKind; label: string }[] = [
  { kind: 'sc', label: 'Sc' },
  { kind: 'cc', label: 'Cc' },
  { kind: 'phSc', label: 'Ph Sc' },
  { kind: 'phCc', label: 'Ph Cc' },
];
const NEAR_KINDS: { kind: PairKind; label: string }[] = [
  { kind: 'sc', label: 'Sc' },
  { kind: 'cc', label: 'Cc' },
];

const DENOMINATOR_OPTIONS = [
  { label: '—', value: '' },
  ...ALLOWED_DENOMINATOR.map((d) => ({ label: d, value: d })),
];

/** Immutably set one numerator/denominator field, keeping the rest of the tree intact. */
function setField(
  data: VisualAcuityData,
  section: Section,
  eye: Eye,
  kind: PairKind,
  field: keyof NumeratorDenominator,
  value: string,
): VisualAcuityData {
  const range = data[section] ?? {};
  const eyeData: EyeData = range[eye] ?? emptyEye();
  const pair: NumeratorDenominator = { ...(eyeData[kind] ?? {}), [field]: value };
  return {
    ...data,
    [section]: { ...range, [eye]: { ...eyeData, [kind]: pair } },
  };
}

export function VisualAcuityForm({ value, onChange }: VisualAcuityFormProps) {
  const pair = (section: Section, eye: Eye, kind: PairKind): NumeratorDenominator =>
    value[section]?.[eye]?.[kind] ?? {};

  const renderEye = (
    section: Section,
    eye: Eye,
    label: string,
    kinds: { kind: PairKind; label: string }[],
  ) => (
    <div className="min-w-50">
      <p className={EYE_LABEL} data-part="eye-label">{label}</p>
      {kinds.map(({ kind, label: kindLabel }) => {
        const p = pair(section, eye, kind);
        return (
          <div key={kind} className={PAIR_ROW}>
            <TextField
              aria-label={`${label} ${kindLabel} numerator`}
              size="compact"
              inputMode="numeric"
              maxLength={2}
              value={p.numerator ?? ''}
              onChange={(e) => onChange(setField(value, section, eye, kind, 'numerator', e.target.value))}
              placeholder="20"
              containerClassName="w-14 shrink-0"
              inputClassName="text-center"
            />
            <span className={SLASH} data-part="slash">/</span>
            <Select
              aria-label={`${label} ${kindLabel} denominator`}
              size="compact"
              options={DENOMINATOR_OPTIONS}
              value={p.denominator ?? ''}
              onChange={(v) => onChange(setField(value, section, eye, kind, 'denominator', v))}
              className="w-20 shrink-0"
            />
            <span className={KIND_LABEL} data-part="kind">{kindLabel}</span>
          </div>
        );
      })}
    </div>
  );

  const renderSection = (section: Section, title: string, kinds: { kind: PairKind; label: string }[]) => (
    <div className="mb-4">
      <p className={SECTION_TITLE} data-part="section-title">{title}</p>
      <div className="flex gap-6 flex-wrap">
        {renderEye(section, 'leftEye', 'Left eye (OS)', kinds)}
        {renderEye(section, 'rightEye', 'Right eye (OD)', kinds)}
      </div>
    </div>
  );

  return (
    <div className="font-sans" data-slot="va-form">
      {renderSection('distance', 'Distance', DISTANCE_KINDS)}
      {renderSection('near', 'Near', NEAR_KINDS)}
      <div>
        <p className={SECTION_TITLE} data-part="section-title">Correction type</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented
            // Keyed remount on clear: DS Segmented falls back to its internal state when
            // `value` returns to undefined, which would keep the old segment lit.
            key={value.correctionType ?? 'unset'}
            aria-label="Correction type"
            options={CORRECTION_TYPES.map((ct) => ({ label: ct, value: ct }))}
            value={(value.correctionType ?? undefined) as CorrectionType | undefined}
            onChange={(ct) => onChange({ ...value, correctionType: ct })}
          />
          {/* Explicit unset path (Codex review, 2026-07-23): "No Correction" is a clinical
              ASSERTION — null (unrecorded) must stay reachable after a selection. */}
          {value.correctionType != null ? (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => onChange({ ...value, correctionType: null })}
            >
              Clear
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
